import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { api } from "@/lib/api";
import { bootProfilePrefs } from "@/lib/profilePrefs";
import {
  bootLang,
  isLang,
  onFullMessages,
  readStoredLang,
  shouldAdoptAccountPref,
  STORAGE_KEY,
  translate,
  type Lang,
  type TFn,
} from "./core";

export {
  bootLang,
  DEFAULT_LANG,
  isLang,
  LANGS,
  LANG_LABELS,
  loadFullMessages,
  shouldAdoptAccountPref,
  translate,
} from "./core";
export type { Lang, TFn } from "./core";

interface LanguageContextValue {
  lang: Lang;
  setLang: (l: Lang) => void;
  toggle: () => void;
  t: TFn;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

/**
 * Dashboard/admin UI language. Source of truth is localStorage (read
 * synchronously at first paint, so a refresh never flips the choice back). The
 * account's saved `ui_language` is only adopted on a fresh device where no local
 * choice exists yet; a toggle writes both localStorage and the profile so the
 * preference is durable and follows the account across devices.
 */
export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => bootLang(readStoredLang()));
  const hasLocalChoice = useRef(readStoredLang() !== null);

  useEffect(() => {
    if (hasLocalChoice.current) return;
    let active = true;
    bootProfilePrefs()
      .then((r) => {
        const pref = isLang(r.uiLanguage) ? r.uiLanguage : null;
        if (active && shouldAdoptAccountPref(readStoredLang(), pref) && pref) {
          setLangState(pref);
          try {
            localStorage.setItem(STORAGE_KEY, pref);
          } catch {
            /* private mode — in-memory only */
          }
        }
      })
      .catch(() => {
        /* signed out / no profile — keep the default */
      });
    return () => {
      active = false;
    };
  }, []);

  const setLang = useCallback((next: Lang) => {
    setLangState(next);
    hasLocalChoice.current = true;
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* private mode — in-memory only */
    }
    // Fire-and-forget: a failed sync (signed out, offline) just leaves the
    // account default unchanged; localStorage already holds the live choice.
    api.patch("/profile", { uiLanguage: next }, { optionalAuth: true }).catch(() => {});
  }, []);

  // A new `t` once the full copy lands, so anything that rendered a key before it
  // arrived renders the word instead.
  const [copyLoads, setCopyLoads] = useState(0);
  useEffect(() => onFullMessages(() => setCopyLoads((n) => n + 1)), []);

  const value = useMemo<LanguageContextValue>(
    () => ({
      lang,
      setLang,
      toggle: () => setLang(lang === "ms" ? "en" : "ms"),
      t: (key, vars) => translate(lang, key, vars),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- copyLoads only renews `t`
    [lang, setLang, copyLoads],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useLang(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be used within LanguageProvider");
  return ctx;
}

/** Convenience hook for components that only need the translate function. */
// eslint-disable-next-line react-refresh/only-export-components
export function useT(): TFn {
  return useLang().t;
}
